import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { env } from "@/env";
import { queryKeys } from "@/lib/query-keys";

const POLL_FALLBACK_INTERVAL_MS = 5000;
const ERROR_THRESHOLD = 2;

export type RiderEvent = {
  type:
    | "order.status_changed"
    | "order.payment_updated"
    | "delivery.assigned"
    | "delivery.state_changed";
  timestamp: string;
  organizationId: string;
  orderId: string;
  deliveryId?: string;
  riderId?: string;
  data: {
    status?: string;
    paymentStatus?: string;
    deliveryState?: string;
    [key: string]: unknown;
  };
};

export function useRiderEvents(onEvent?: (event: RiderEvent) => void) {
  const [connected, setConnected] = useState(false);
  const [pollingFallback, setPollingFallback] = useState(false);
  const queryClient = useQueryClient();
  const errorCountRef = useRef(0);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    const url = `${env.VITE_API_URL}/api/deliveries/events`;
    let stopped = false;
    let pollingTimer: ReturnType<typeof setInterval> | null = null;

    function startPolling() {
      if (pollingTimer) {
        return;
      }
      setPollingFallback(true);
      pollingTimer = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() });
      }, POLL_FALLBACK_INTERVAL_MS);
    }

    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      setConnected(true);
      errorCountRef.current = 0;
    };

    eventSource.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data) as RiderEvent;
        queryClient.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() });
        onEventRef.current?.(parsed);
      } catch {
        // ignore parse errors
      }
    };

    eventSource.onerror = () => {
      setConnected(false);
      errorCountRef.current += 1;
      if (errorCountRef.current >= ERROR_THRESHOLD && !stopped) {
        eventSource.close();
        startPolling();
      }
    };

    return () => {
      stopped = true;
      eventSource.close();
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [queryClient]);

  return { connected, pollingFallback };
}
